import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
  Ref,
  SyntheticEvent,
  forwardRef,
  type JSX,
} from 'react';

import reactToText from 'react-to-text';

import * as s from './TextLink.css';
import { ExternalIcon } from '../../icons';
import { LinkColors, TextColors } from '../../types';
import { extractDataProps } from '../../util/attributes';
import { getClassNames } from '../../util/css';
import { Text } from '../Text/Text';

const sparkyClassName = 'sparky-text-link';

type TextLinkButtonProps = {
  href?: never;
  target?: never;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

type TextLinkAnchorProps = {
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  type?: never;
};

type EmphasisLowType = {
  emphasis?: 'low';
  color?: keyof TextColors;
};

type EmphasisMediumType = {
  emphasis: 'medium';
  color?: never;
};

type EmphasisHighType = {
  emphasis: 'high';
  color?: keyof Pick<LinkColors, 'linkBrand' | 'linkPrimary' | 'linkInverted'>;
};

type EmphasisProps = EmphasisLowType | EmphasisMediumType | EmphasisHighType;

export type TextLinkProps = {
  onClick?(e: SyntheticEvent): void;
  children: ReactNode;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;

  className?: never;

  ariaLabel?: string;
} & (TextLinkAnchorProps | TextLinkButtonProps) &
  EmphasisProps;

export const TextLink = forwardRef<HTMLAnchorElement | HTMLButtonElement, TextLinkProps>(
  (
    {
      ariaLabel,
      children,
      className = '',
      color,
      emphasis = 'low',
      href,
      leftIcon,
      onClick,
      rightIcon,
      target = '',
      type = 'button',
      ...rest
    },
    ref,
  ) => {
    const dataSet = extractDataProps(rest);
    const isExternalLink = target === '_blank';

    const styleColor = color ? { color: `var(--colors-${String(color)})` } : undefined;

    const linkClass = getClassNames(
      sparkyClassName,
      s.linkBase,
      s.emphasis[emphasis],
      s.focusOutline,
      s.focusOutlineFallback,
      emphasis === 'high' && s.highEmphasisOutline,
      className,
    );

    const textClass = getClassNames(s.text, s.textUnderlineOnHover, s.textForEmphasis[emphasis]);

    const containerClass = getClassNames(
      s.container,
      leftIcon && s.containerHasLeftIcon,
      (!!rightIcon || isExternalLink) && s.containerHasRightIcon,
    );

    const linkContent = (
      <>
        {leftIcon}
        <Text display="inline" className={containerClass}>
          <span className={textClass}>{children}</span>
        </Text>
        {isExternalLink ? <ExternalIcon color="currentColor" size="small" verticalAlign="text-bottom" /> : rightIcon}
      </>
    );

    const commonProps = {
      ...dataSet,
      'aria-label': ariaLabel,
      className: linkClass,
      'data-label': ariaLabel ?? reactToText(children),
      onClick,
      style: styleColor,
    };

    return href ? (
      <a
        {...commonProps}
        href={href}
        target={target || undefined}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        ref={ref as Ref<HTMLAnchorElement>}>
        {linkContent}
      </a>
    ) : (
      <button {...commonProps} type={type} ref={ref as Ref<HTMLButtonElement>}>
        {linkContent}
      </button>
    );
  },
);

TextLink.toString = () => `.${sparkyClassName}`;
TextLink.displayName = 'TextLink';
